import React, {createContext, useCallback, useContext, useMemo} from 'react';

import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {parseMCPToolName} from 'llm/chat-library/tool-details/types';

import type {MCPToolMappingQuery} from './__generated/MCPToolMappingQuery';
import {MCP_TOOL_MAPPING_QUERY} from './MCPToolMappingQuery';

/**
 * Context value interface for MCP tool display name mapping.
 */
export interface MCPToolMappingContextValue {
  /** True while the mapping data is being fetched */
  isLoading: boolean;

  /**
   * Get the display name for an MCP server given its API ID.
   * Falls back to the raw API ID if not found.
   */
  getServerDisplayName: (serverApiId: string) => string;

  /**
   * Get the full display name for a namespaced MCP tool.
   * Input format: "serverApiId__toolName"
   * Output format: "Server Name: Tool Title" (if title available)
   * Falls back to "Server Name: toolName" if title is unavailable.
   * Falls back to "serverApiId: toolName" if server mapping is unavailable.
   */
  getFullToolDisplayName: (namespacedToolName: string) => string;
}

const MCPToolMappingContext = createContext<MCPToolMappingContextValue | null>(null);

interface MCPToolMappingProviderProps {
  children: React.ReactNode;
}

/**
 * Provider that fetches and caches MCP server display name mappings.
 * Wrap chat components that display MCP tool names with this provider.
 *
 * Behavior during loading and error states:
 * - While loading (isLoading = true): Returns empty mapping, consumers fall back to API IDs
 * - On error: Returns empty mapping, consumers fall back to API IDs
 * - On success: Returns full tenant-level server name mapping from MCP server configs
 *
 * The provider uses cache-first fetch policy, so subsequent renders will use cached data.
 */
export function MCPToolMappingProvider({children}: MCPToolMappingProviderProps): JSX.Element {
  const queryResult = useBenchlingQuery<MCPToolMappingQuery>(MCP_TOOL_MAPPING_QUERY, {
    fetchPolicy: 'cache-first',
  });

  const isLoading = queryResult.type === QueryResultType.PENDING;

  // Build mapping from server API ID to server display name
  // Returns empty map if query is pending, failed, or not yet loaded
  // This ensures graceful fallback to API IDs in consumers
  const serverNameMap = useMemo((): Map<string, string> => {
    if (queryResult.type !== QueryResultType.SUCCESS) {
      return new Map();
    }

    return new Map(queryResult.data.mcpServerConfigs.map((config) => [config.id, config.name]));
  }, [queryResult]);

  // Build mapping from namespaced tool name to tool title
  // Returns empty map if query is pending, failed, or not yet loaded
  const toolTitleMap = useMemo((): Map<string, string> => {
    if (queryResult.type !== QueryResultType.SUCCESS) {
      return new Map();
    }

    const map = new Map<string, string>();
    for (const config of queryResult.data.mcpServerConfigs) {
      for (const tool of config.tools) {
        if (tool.title) {
          // Tool name from GraphQL is already namespaced (e.g., "mcpconf_123__tool_name")
          map.set(tool.name, tool.title);
        }
      }
    }
    return map;
  }, [queryResult]);

  const getServerDisplayName = useCallback(
    (serverApiId: string): string => serverNameMap.get(serverApiId) ?? serverApiId,
    [serverNameMap]
  );

  const getFullToolDisplayName = useCallback(
    (namespacedToolName: string): string => {
      const {serverName: serverApiId, originalToolName} = parseMCPToolName(namespacedToolName);

      if (!serverApiId) {
        // Not an MCP tool or invalid format
        return namespacedToolName;
      }

      const serverDisplayName = getServerDisplayName(serverApiId);
      // Use tool title if available, otherwise fall back to original tool name
      const toolDisplayName = toolTitleMap.get(namespacedToolName) ?? originalToolName;
      return `${serverDisplayName}: ${toolDisplayName}`;
    },
    [getServerDisplayName, toolTitleMap]
  );

  const contextValue = useMemo(
    (): MCPToolMappingContextValue => ({
      isLoading,
      getServerDisplayName,
      getFullToolDisplayName,
    }),
    [isLoading, getServerDisplayName, getFullToolDisplayName]
  );

  return <MCPToolMappingContext.Provider value={contextValue}>{children}</MCPToolMappingContext.Provider>;
}

/**
 * Hook to access MCP tool display name mapping.
 * Returns null if used outside of MCPToolMappingProvider.
 * Components using this hook should handle the null case gracefully by falling back to basic display names.
 */
export function useMCPToolMappingOptional(): MCPToolMappingContextValue | null {
  return useContext(MCPToolMappingContext);
}
